import { useState, useEffect } from 'react';
import { guildsAPI } from '../../api';
import api from '../../api';
import ChannelSelect from '../../components/ui/ChannelSelect';

const DEFAULT_BANNERS = [
  {
    id: 'pink_office',
    name: 'Brazyy Pink',
    description: 'Soft pink workspace — cozy and welcoming',
    preview: '🌸',
    url: 'https://i.postimg.cc/BvncK0hV/Copilot-20260607-221130.png',
  },
  {
    id: 'midnight_gold',
    name: 'Midnight Gold',
    description: 'Dark dramatic with golden gears',
    preview: '✨',
    url: 'https://i.postimg.cc/bY1LDb1Y/midnight-clock.png',
  },
];

export default function PanelPage({ guild, setGuild }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState('custom');
  const [form, setForm] = useState({
    panel_image_url: '',
    shift_notes_enabled: true,
    clockin_cooldown_minutes: 2,
    panel_channel_id: '',
    announcements_channel_id: '',
    status_channel_id: '',
    log_channel_id: '',
    adjustment_channel_id: '',
    panel_message_id: '',
  });

  useEffect(() => {
    guildsAPI.getOne(guild.id)
      .then(res => {
        const d = res.data;
        setForm({
          panel_image_url: d.panel_image_url || '',
          shift_notes_enabled: d.shift_notes_enabled ?? true,
          clockin_cooldown_minutes: d.clockin_cooldown_minutes ?? 2,
          panel_channel_id: d.panel_channel_id || '',
          announcements_channel_id: d.announcements_channel_id || '',
          status_channel_id: d.status_channel_id || '',
          log_channel_id: d.log_channel_id || '',
          adjustment_channel_id: d.adjustment_channel_id || '',
          panel_message_id: d.panel_message_id || '',
        });
        // Detect if current url matches a default banner
        const match = DEFAULT_BANNERS.find(b => b.url === d.panel_image_url);
        setSelectedBanner(match ? match.id : 'custom');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleBannerSelect = (banner) => {
    setSelectedBanner(banner.id);
    update('panel_image_url', banner.url);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await guildsAPI.update(guild.id, form);
      if (setGuild) setGuild(prev => ({ ...prev, ...res.data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const handlePushToDiscord = async () => {
    if (!form.panel_channel_id) return alert('Please select a Panel Channel first, then save.');
    setPushing(true);
    setPushResult(null);
    try {
      const res = await api.post(`/api/discord/${guild.id}/update-panel`, {
        channel_id: form.panel_channel_id,
        message_id: form.panel_message_id || null,
        panel_image_url: form.panel_image_url || null,
      });
      // Save the message ID so future pushes edit instead of repost
      if (res.data.message_id && res.data.message_id !== form.panel_message_id) {
        const newForm = { ...form, panel_message_id: res.data.message_id };
        setForm(newForm);
        await guildsAPI.update(guild.id, { panel_message_id: res.data.message_id });
      }
      setPushResult({ success: true, msg: form.panel_message_id ? 'Panel updated in Discord!' : 'Panel posted to Discord!' });
    } catch (err) {
      setPushResult({ success: false, msg: err.response?.data?.error || 'Failed to push to Discord.' });
    } finally {
      setPushing(false);
      setTimeout(() => setPushResult(null), 5000);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading panel settings...
    </div>
  );

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Panel Settings</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Configure your Discord clock-in panel</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {pushResult && (
            <span style={{ fontSize: '13px', color: pushResult.success ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
              {pushResult.success ? '✓' : '✗'} {pushResult.msg}
            </span>
          )}
          <button onClick={handlePushToDiscord} disabled={pushing} style={{
            padding: '12px 20px', background: pushing ? 'var(--text-muted)' : '#5865F2',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '14px', fontWeight: 700, cursor: pushing ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-display)',
          }}>
            {pushing ? 'Pushing...' : form.panel_message_id ? '🔄 Update Panel' : '📤 Push to Discord'}
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '12px 24px', background: saved ? 'var(--success)' : 'var(--gradient-accent)',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
          }}>
            {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Banner selection */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '6px' }}>🖼️ Panel Banner</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>The image shown at the top of your Discord clock-in panel</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              {DEFAULT_BANNERS.map(banner => (
                <div key={banner.id} onClick={() => handleBannerSelect(banner)}
                  style={{
                    padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                    border: `2px solid ${selectedBanner === banner.id ? 'var(--accent-primary)' : 'var(--border)'}`,
                    background: selectedBanner === banner.id ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                    display: 'flex', alignItems: 'center', gap: '14px',
                    transition: 'all 0.15s',
                  }}>
                  <span style={{ fontSize: '28px', flexShrink: 0 }}>{banner.preview}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{banner.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{banner.description}</div>
                  </div>
                  {selectedBanner === banner.id && <span style={{ color: 'var(--accent-primary)', fontWeight: 700 }}>✓</span>}
                </div>
              ))}

              {/* Custom option */}
              <div onClick={() => setSelectedBanner('custom')}
                style={{
                  padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                  border: `2px solid ${selectedBanner === 'custom' ? 'var(--accent-primary)' : 'var(--border)'}`,
                  background: selectedBanner === 'custom' ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                  display: 'flex', alignItems: 'center', gap: '14px',
                  transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: '28px', flexShrink: 0 }}>🖼️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Custom URL</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Use your own image or GIF</div>
                </div>
                {selectedBanner === 'custom' && <span style={{ color: 'var(--accent-primary)', fontWeight: 700 }}>✓</span>}
              </div>
            </div>

            {/* Custom URL input — always visible */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                {selectedBanner === 'custom' ? 'Custom Image/GIF URL' : 'Override URL (optional)'}
              </label>
              <input type="url"
                placeholder="https://example.com/banner.gif"
                value={selectedBanner === 'custom' ? form.panel_image_url : ''}
                onChange={e => { setSelectedBanner('custom'); update('panel_image_url', e.target.value); }}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Supports PNG, JPG, GIF. Recommended: 1200×400px wide banner.
              </div>
            </div>
          </div>

          {/* Panel behavior */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '20px' }}>⚙️ Panel Behavior</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Shift Notes</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Prompt staff for notes when they clock out</div>
                </div>
                <button onClick={() => update('shift_notes_enabled', !form.shift_notes_enabled)} style={{
                  width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                  background: form.shift_notes_enabled ? 'var(--success)' : 'var(--border)',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.shift_notes_enabled ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </button>
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Clock-In Cooldown (minutes)</label>
                <input type="number" min="0" value={form.clockin_cooldown_minutes}
                  onChange={e => update('clockin_cooldown_minutes', parseInt(e.target.value) || 0)}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                />
              </div>
            </div>
          </div>

          {/* Channel configuration */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '6px' }}>📢 Channel Setup</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Only channels the bot can see will appear in the dropdowns.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { key: 'panel_channel_id', label: '🕐 Clock-In Panel Channel', hint: 'Where the panel embed is posted' },
                { key: 'announcements_channel_id', label: '📣 Announcements Channel', hint: 'Bonuses, paydays, birthdays' },
                { key: 'status_channel_id', label: '📊 Live Status Channel', hint: 'Auto-updating status embed' },
                { key: 'log_channel_id', label: '📋 Audit Log Channel', hint: 'Clock-in/out and action logs' },
                { key: 'adjustment_channel_id', label: '⚖️ Adjustments Channel', hint: 'Time adjustment requests' },
              ].map(field => (
                <div key={field.key}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    {field.label}
                  </label>
                  <ChannelSelect
                    guildId={guild.id}
                    value={form[field.key]}
                    onChange={val => update(field.key, val)}
                    placeholder={`Select channel...`}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>{field.hint}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div style={{ position: 'sticky', top: '80px', height: 'fit-content', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>Discord Preview</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Approximate appearance in Discord</div>
            </div>
            <div style={{ padding: '14px', background: '#36393f' }}>
              <div style={{ background: '#2f3136', borderRadius: '6px', overflow: 'hidden', borderLeft: '4px solid #E8341C' }}>
                {form.panel_image_url ? (
                  <img src={form.panel_image_url} alt="Banner preview"
                    style={{ width: '100%', maxHeight: '100px', objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '60px', background: 'linear-gradient(135deg, #E8341C, #F5A623)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🕐</div>
                )}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>Brazyy's Clock-In</div>
                  <div style={{ fontSize: '12px', color: '#b9bbbe', marginBottom: '10px' }}>Click a button to clock in or out.</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <div style={{ padding: '6px 12px', background: '#57F287', borderRadius: '4px', fontSize: '11px', fontWeight: 700, color: '#000' }}>🟢 Clock In</div>
                    <div style={{ padding: '6px 12px', background: '#ED4245', borderRadius: '4px', fontSize: '11px', fontWeight: 700, color: '#fff' }}>🔴 Clock Out</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text-primary)' }}>💡 How it works:</strong><br />
            Save your settings first, then click <strong>Push to Discord</strong>. If a panel already exists in the channel it will be updated in place. If not, a new one will be posted.
          </div>
        </div>
      </div>
    </div>
  );
}
